import dayjs from "dayjs";
import { useMemo } from "react";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useSellarDistributionAvailabilityDeliveryEstimates } from "../api/v2/sellarDistributionAvailability/useSellarDistributionAvailabilityDeliveryEstimates";
import { SellarDistributionCutoffPolicy } from "@sellar-io/api-v2/types";

dayjs.extend(utc);
dayjs.extend(timezone);

export const useDistributionDeliveryEstimate = ({
  supplierId,
  productIds,
  buyerPostcode,
  enabled = true,
  cutoffPolicy = "buyerOrderPlacement",
}: {
  supplierId: number;
  productIds: number[];
  buyerPostcode: string;
  enabled?: boolean;
  cutoffPolicy?: SellarDistributionCutoffPolicy;
}) => {
  const sellarDistributionAvailabilityDeliveryEstimatesQuery =
    useSellarDistributionAvailabilityDeliveryEstimates(
      {
        query: [
          {
            type: "supplierId",
            value: supplierId,
            modifier: "EQUALS",
          },
          {
            type: "cutoffPolicy",
            modifier: "EQUALS",
            value: cutoffPolicy,
          },
          {
            type: "retailerPostcode",
            value: buyerPostcode,
            modifier: "EQUALS",
          },
          ...(productIds.length > 0
            ? ([
                {
                  type: "productIds",
                  value: productIds,
                  modifier: "EQUALS",
                },
              ] as const)
            : []),
        ],
      },
      {
        enabled: !!buyerPostcode && !!supplierId && enabled,
      },
    );

  const sdDeliveryDate = sellarDistributionAvailabilityDeliveryEstimatesQuery
    .data?.nextDeliveryAt
    ? dayjs(
        sellarDistributionAvailabilityDeliveryEstimatesQuery.data
          .nextDeliveryAt,
      )
        .tz("Europe/London")
        .toDate()
    : null;

  const sdDispatchDate = sellarDistributionAvailabilityDeliveryEstimatesQuery
    .data?.nextDispatchAt
    ? dayjs(
        sellarDistributionAvailabilityDeliveryEstimatesQuery.data
          .nextDispatchAt,
      )
        .tz("Europe/London")
        .toDate()
    : null;

  const sdOrderByDate = sellarDistributionAvailabilityDeliveryEstimatesQuery
    .data?.orderBy
    ? dayjs(sellarDistributionAvailabilityDeliveryEstimatesQuery.data.orderBy)
        .tz("Europe/London")
        .toDate()
    : null;

  const value = useMemo(() => {
    return {
      nextDeliveryDate: sdDeliveryDate,
      nextDispatchDate: sdDispatchDate,
      orderByDate: sdOrderByDate,
      isFetching:
        sellarDistributionAvailabilityDeliveryEstimatesQuery.isFetching,
    };
  }, [
    sdDeliveryDate,
    sdDispatchDate,
    sdOrderByDate,
    sellarDistributionAvailabilityDeliveryEstimatesQuery.isFetching,
  ]);

  return value;
};
